import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { Box, Button, Link, Radio, Text } from '@factorialco/deprecated-design-system'
import { CreateSurveyPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import AccessGroup from 'modules/core/store/models/AccessGroup'

import getCurrentClimate from 'modules/engagement_insights/lib/getCurrentClimate'
import AccessGroupForm from 'modules/surveys/components/AccessGroupForm'
import { Options, Types } from 'modules/surveys/lib/enums'
import type Survey from 'modules/surveys/store/models/v2/Survey'

export default function CreateSurveyPage() {
  const SURVEY: Record<'climate', { title: string; description: string; source: string }> = {
    climate: {
      title: i18n.t('surveys.creation.templates.climate.new_title'),
      description: i18n.t('surveys.creation.templates.climate.description'),
      source: 'climate_survey',
    },
  }
  const isAdmin = false

  const [climate, setClimate] = React.useState<Survey>()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const { data, loading: loadingPolicies } = useQuery(CreateSurveyPageDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const canCreateForms = data?.surveys._policies.createForms.can ?? false

  const { has_surveys_using_forms: smartSurveysEnabled } = checkFeatures(
    ['surveys_using_forms'],
    data?.features?.featuresConnection.nodes
  )

  const [createOption, setCreateOption] = React.useState<Options>('template')

  const [, setLoading] = React.useState(false)

  const surveyId = climate?.attributes?.id || ''

  const accessGroupForm = useLazyRef(
    () =>
      new AccessGroupForm({
        strategy: null,
        groupable_id: null,
        groupable_type: null,
      })
  ).current

  accessGroupForm.setMembers([])
  accessGroupForm.get('strategy').set(AccessGroup.Strategies.withRegularAccess)

  const fetchClimate = async () => {
    if (!isAdmin) {
      setCreateOption('custom')

      return
    }

    const survey = await getCurrentClimate()

    setClimate(survey)

    if (survey) {
      setCreateOption('custom')
    }
  }

  const { loading: loadingClimate } = useAsyncData(() => fetchClimate(), [])

  const onClose = () => {
    track(trackingEvents.surveys.createSurveyCancel, { exit_step: 1 })
    navigate(navigation.processes.surveys.fullPath())
  }

  const onSubmit = async () => {
    track(trackingEvents.surveys.createSurveyNextStep)

    switch (createOption) {
      case 'custom':
        navigate(navigation.processes.surveys.new.fullPath())
        break
      case 'workflow':
        navigate(navigation.processes.surveys.newWorkflowSurvey.fullPath())
        break
      default:
        await createClimateSurvey()
        break
    }
  }

  const createClimateSurvey = async () => {
    setLoading(true)

    const currentSurvey = SURVEY['climate']

    const { success, value } = await asyncAction(
      () =>
        ApiClient().post('/surveys/v2/surveys/create_from_template', {
          name: currentSurvey.title,
          description: currentSurvey?.description,
          source: currentSurvey?.source,
          question_group_config: accessGroupForm.data(),
          start_on: moment().startOf('day').format('YYYY-MM-DD'),
          frequency: 'quarterly',
        }),
      {
        successMessage: i18n.t('common.saved_successfully'),
        errorFlash: true,
      }
    )

    setLoading(false)

    if (success) {
      track(trackingEvents.surveys.createSurveySubmit, { type: Types.climate })
      navigate(navigation.processes.surveys.id.configuration.fullPath({ id: value.id }))
    }
  }

  const getClimateDescription = () => {
    if (!climate) {
      return i18n.t('surveys.creation.templates.climate.new_create_description')
    }

    if (climate.isDraft) {
      return i18n.t('surveys.creation.templates.climate.draft_description')
    }

    if (climate.isActive) {
      return i18n.t('surveys.creation.templates.climate.active_description')
    }

    if (climate.isFinished) {
      return i18n.t('surveys.creation.templates.climate.stopped_description')
    }

    if (climate.isScheduled) {
      return i18n.t('surveys.creation.templates.climate.scheduled_description', {
        scheduled_date: moment(climate?.attributes.start_on).format('Do MMMM'),
      })
    }
  }

  const ClimateDescription = () => {
    return (
      <Box gap="s8">
        {getClimateDescription()}
        {!!climate && (
          <Box width="fit-content">
            <Link.Touchable onClick={() => (surveyId ? goToClimate() : null)}>
              <Text.Normal color="grey1000" weight="medium">
                {i18n.t('surveys.creation.go_to_survey')}
              </Text.Normal>
            </Link.Touchable>
          </Box>
        )}
      </Box>
    )
  }

  const FooterButton = () => {
    const btnText =
      createOption === 'template' && !climate && !loadingClimate
        ? i18n.t('common.create')
        : i18n.t('common.continue')

    return <Button submit>{btnText}</Button>
  }

  const goToClimate = () => {
    navigate(navigation.processes.surveys.id.analytics.fullPath({ id: surveyId }))
    track('Go to Climate')
  }

  if (!loadingPolicies) {
    if (!canCreateForms) {
      return <Navigate to={navigation.processes.surveys.fullPath()} />
    } else if (smartSurveysEnabled) {
      return <Navigate to={navigation.processes.surveys.newWorkflowSurvey.fullPath()} />
    }
  }

  return (
    <Modal
      title={i18n.t('surveys.creation.title')}
      description={i18n.t('surveys.creation.description')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={<FooterButton />}
    >
      {loadingClimate || loadingPolicies ? (
        <Loading />
      ) : (
        <>
          {isAdmin && (
            <Radio.Wrap>
              <Radio
                name="create_mode"
                value={createOption}
                onChange={() => setCreateOption('template')}
                description={<ClimateDescription />}
                option={{
                  label: SURVEY['climate'].title,
                  value: climate ? '' : 'template',
                  disabled: !!climate,
                }}
                aria-label="Use climate survey template setup"
              ></Radio>
            </Radio.Wrap>
          )}
          <Radio.Wrap>
            <Radio
              name="create_mode"
              value={createOption}
              onChange={() => setCreateOption('custom')}
              description={i18n.t('surveys.creation.custom_description')}
              option={{
                label: i18n.t('surveys.creation.custom_title'),
                value: 'custom',
              }}
              aria-label="Use custom survey setup"
            ></Radio>
          </Radio.Wrap>
          <Radio.Wrap>
            <Radio
              name="create_mode"
              value={createOption}
              onChange={() => setCreateOption('workflow')}
              description={i18n.t('surveys.creation.workflow_survey_description')}
              option={{
                label: i18n.t('surveys.creation.workflow_survey'),
                value: 'workflow',
              }}
              aria-label="Use workflow survey setup"
            />
          </Radio.Wrap>
        </>
      )}
    </Modal>
  )
}
